import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Course, Module, Lesson } from '../lib/types'; 
import { fetchCourse } from '@/lib/courses'; 
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '../components/auth/AuthProvider'; 

export function useCourseDetails(slug?: string, userId?: string, lang: string = 'en') {
  const params = useParams<{ slug: string }>();
  const courseSlug = slug || params.slug;
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const currentUserId = userId || user?.id;

  const [course, setCourse] = useState<Course | null>(null);
  const [modules, setModules] = useState<Module[]>([]);
  const [lessons, setLessons] = useState<Record<string, Lesson[]>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [hasAccess, setHasAccess] = useState<boolean>(false);
  const [isPurchased, setIsPurchased] = useState<boolean>(false);

  const loadCourse = useCallback(async () => {
    setLoading(true);
    setCourse(null); 
    setModules([]);
    setLessons({});
    setHasAccess(false);
    setIsPurchased(false);
    
    if (!courseSlug) {
      console.warn('[useCourseDetails] No slug provided, navigating to /courses');
      navigate(`/${lang === 'en' ? '' : lang + '/'}courses`);
      setLoading(false);
      return;
    }
    
    const result = await fetchCourse(courseSlug, lang); 
    
    if (!result) {
      console.error(`[useCourseDetails] Error fetching course markdown for slug: ${courseSlug}`);
      toast({ title: "Error", description: "Could not load course content.", variant: "destructive" });
      navigate(`/${lang === 'en' ? '' : lang + '/'}courses`);
      setLoading(false);
      return;
    }
    
    setCourse(result); 
    setModules(result.modules);
    
    // Transform lessons object to match expected Record<string, Lesson[]> format
    const lessonsMap: Record<string, Lesson[]> = {};
    result.modules.forEach(module => {
      // For each module, get its lessons from the result.lessons object
      const moduleLessons: Lesson[] = [];
      module.lessons.forEach(lesson => {
        const fullLesson = result.lessons[lesson.slug];
        if (fullLesson) {
          // If fullLesson is an array, spread it; otherwise push the single lesson
          if (Array.isArray(fullLesson)) {
            moduleLessons.push(...fullLesson);
          } else {
            moduleLessons.push(fullLesson);
          }
        }
      });
      lessonsMap[module.id] = moduleLessons;
    });
    
    setLessons(lessonsMap);

    // For paid courses, check if user has purchased via user_course_access table
    if (courseSlug && result && !result.is_free && currentUserId) { 
      try {
        console.log(`[useCourseDetails] Checking purchase status for paid course: ${courseSlug}`);
        const { count, error } = await supabase
          .from("user_course_access")
          .select("user_id", { count: 'exact', head: true})
          .eq("user_id", currentUserId)
          .eq("course_slug", courseSlug);
          
        if (error) {
          console.error("[useCourseDetails] Error checking paid course access:", error);
          setIsPurchased(false);
        } else {
          setIsPurchased((count ?? 0) > 0);
        }
      } catch (err) {
        console.error(`[useCourseDetails] Failed to check purchase status for ${courseSlug}:`, err);
        setIsPurchased(false);
      }
    }

    if (currentUserId && result) { 
      if (result.is_free) {
        // For free courses, only check if user is already enrolled - don't auto-grant access
        try {
          // The query with head: true, count: 'exact' returns { count: number | null }
          const { count, error: checkError } = await supabase
            .from("user_course_access")
            .select("user_id", { count: 'exact', head: true})
            .eq("user_id", currentUserId)
            .eq("course_slug", courseSlug);
            
          if (checkError) {
            console.error("[useCourseDetails] Error checking course access:", checkError);
            setHasAccess(false);
          } else {
            // Only grant access if user is already enrolled
            setHasAccess((count ?? 0) > 0);
          }
        } catch (error) {
          console.error("[useCourseDetails] Error checking access to free course:", error);
          setHasAccess(false);
        }
      } else {
        // Check access for paid course
        // The query with head: true, count: 'exact' returns { count: number | null }
        const { count, error: accessError } = await supabase
          .from("user_course_access")
          .select("user_id", { count: 'exact', head: true})
          .eq("user_id", currentUserId)
          .eq("course_slug", courseSlug);
          
        if (accessError) {
          console.error("[useCourseDetails] Error checking paid course access:", accessError);
          setHasAccess(false);
        } else {
          // Check the count directly
          setHasAccess((count ?? 0) > 0);
        }
      }
    }
    
    setLoading(false);
  }, [courseSlug, navigate, toast, currentUserId]);

  useEffect(() => {
    loadCourse();
  }, [loadCourse]); 

  return { 
    course, 
    modules, 
    lessons, 
    loading, 
    hasAccess, 
    setHasAccess,
    isPurchased 
  };
}
